import { useQueries } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { useEphemeralWorkspaceState } from "@/shared/components/visualization/WorkspaceCtx/hooks";
import {
  AssociationType,
  fileAssociationType,
} from "@/shared/domain/association";
import type { DeviceTypeRecord } from "@/shared/domain/deviceTypes";
import { type FileRecord } from "@/shared/domain/files";
import { fileQueryKeys } from "@/shared/domain/files/hooks";
import { sessionQueryKeys } from "@/shared/domain/sessions/hooks/sessionQueryKeys";
import { ingestableAssetFileTypes } from "@/shared/domain/urdf";
import { Banner, Spinner } from "@/shared/foundation/primitives";

import styles from "./ModelFilePicker.module.css";
import { ModelFileRow } from "./ModelFileRow";

/** A device the workspace's data was recorded by, and the org that owns it. */
interface WorkspaceDevice {
  /** The customer's device ID, as datasets, files and sessions carry it. */
  deviceId: string;
  orgId: string;
}

/**
 * The devices behind the data loaded into the workspace, each once.
 *
 * A file's own `device_id` counts first; a file that names none takes its
 * dataset's. The datasets of dataset datasources are already loaded; the
 * dataset of a file datasource is read here, since the workspace holds only
 * the file. A session's devices come from the session's own listing, which
 * the backend builds from the session's files and their datasets as well as
 * from the devices attached to it directly.
 *
 * Devices are org-scoped, so each one is paired with the org of the record
 * that named it rather than the org being browsed.
 */
function useWorkspaceDevices(): {
  devices: WorkspaceDevice[];
  isPending: boolean;
  error: Error | null;
} {
  const { files, datasets, sessions } = useEphemeralWorkspaceState();
  const { datasetService, sessionService } = useDomainServices();

  const loadedDatasetIds = new Set(datasets.map((dataset) => dataset.id));
  const unloadedDatasetIds = [
    ...new Set(
      files
        .filter(
          (file) =>
            !file.device_id &&
            !loadedDatasetIds.has(file.association_id) &&
            fileAssociationType(file.association_id) ===
              AssociationType.Dataset,
        )
        .map((file) => file.association_id),
    ),
  ].sort();

  const datasetQueries = useQueries({
    queries: unloadedDatasetIds.map((datasetId) => ({
      // The key `useDataset` reads, so a dataset read anywhere else is reused.
      queryKey: ["datasetRecord", datasetId],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        datasetService.getDataset(datasetId, { abortSignal: signal }),
      staleTime: 60_000,
    })),
  });
  const sessionQueries = useQueries({
    queries: sessions.map((session) => ({
      queryKey: sessionQueryKeys.devices(session.id),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        sessionService.listAllSessionDevices(session.id, {
          abortSignal: signal,
        }),
    })),
  });

  const named = new Map<string, WorkspaceDevice>();
  const name = (deviceId: string | null | undefined, orgId: string) => {
    if (deviceId) {
      named.set(`${orgId}/${deviceId}`, { deviceId, orgId });
    }
  };
  for (const file of files) {
    name(file.device_id, file.org_id);
  }
  for (const dataset of datasets) {
    name(dataset.record.device_id, dataset.record.org_id);
  }
  for (const query of datasetQueries) {
    name(query.data?.device_id, query.data?.org_id ?? "");
  }
  sessions.forEach((session, index) => {
    for (const deviceId of sessionQueries[index].data ?? []) {
      name(deviceId, session.record.org_id);
    }
  });

  const queries = [...datasetQueries, ...sessionQueries];
  return {
    devices: [...named.values()],
    isPending: queries.some((query) => query.isPending),
    error: queries.find((query) => query.error)?.error ?? null,
  };
}

/** What one device offers the picker. */
interface DeviceModels {
  /** The type the device inherits from, read only when an inherited file needs naming. */
  deviceType: DeviceTypeRecord | null;
  /** The device's own `.urdf` and `.ply` files, then its type's that it does not override. */
  files: FileRecord[];
}

/**
 * The robot descriptions and point clouds among each device's configurations,
 * its type's included, one request chain per device: the device record for
 * its `dv_` and `dt_` IDs, then the inherited listing, then the type's record
 * for the name an inherited row shows.
 */
function useDeviceModels(devices: WorkspaceDevice[]) {
  const { devices: deviceService, deviceTypes, files } = useDomainServices();

  return useQueries({
    queries: devices.map(({ deviceId, orgId }) => ({
      queryKey: fileQueryKeys.deviceModels(orgId, deviceId),
      queryFn: async ({
        signal,
      }: {
        signal: AbortSignal;
      }): Promise<DeviceModels> => {
        const device = await deviceService.getDevice(deviceId, {
          abortSignal: signal,
          resourceOwnerId: orgId,
        });
        if (!device.universal_device_id) {
          return { deviceType: null, files: [] };
        }
        const records = await files.getDeviceFilesWithExtensions(
          device.universal_device_id,
          ingestableAssetFileTypes,
          { abortSignal: signal, resourceOwnerId: orgId },
        );
        const inherited = records.some(
          (record) => record.association_id !== device.universal_device_id,
        );
        const deviceType =
          inherited && device.device_type_id
            ? await deviceTypes.getDeviceType(device.device_type_id, {
                abortSignal: signal,
                resourceOwnerId: orgId,
              })
            : null;
        return { deviceType, files: records };
      },
    })),
  });
}

interface DeviceModelListProps {
  /** What is in the search box; the listed files are filtered by it as typed. */
  term: string;
  /** The file whose artifact is being looked up, while that is in flight. */
  resolving: string | undefined;
  /** Whether an unconverted row may be picked to start a conversion. */
  canIngest: boolean;
  onPick: (record: FileRecord) => void;
  onIngest: (record: FileRecord) => void;
}

/**
 * The robot descriptions and point clouds among the configurations of the
 * devices in the workspace, grouped under each device's ID when there is more
 * than one device, or the reason there are none to list.
 *
 * Rows are filtered by `term` as it is typed, against the path, which includes
 * the file's name. Everything a device offers arrives in one listing, so
 * nothing goes back to the server for a term.
 */
export function DeviceModelList({
  term,
  resolving,
  canIngest,
  onPick,
  onIngest,
}: DeviceModelListProps) {
  const workspace = useWorkspaceDevices();
  const models = useDeviceModels(workspace.devices);

  const error =
    workspace.error ?? models.find((query) => query.error)?.error ?? null;
  if (error !== null) {
    return (
      <div className={styles.status}>
        <Banner variant="error">
          Could not list the configurations of the devices in this workspace.
          Try closing and reopening this dialog.
        </Banner>
      </div>
    );
  }

  if (workspace.isPending || models.some((query) => query.isPending)) {
    return (
      <div className={styles.status}>
        <Spinner aria-hidden />
        <RobotoTypography variant="body2">
          Looking for configurations…
        </RobotoTypography>
      </div>
    );
  }

  if (workspace.devices.length === 0) {
    return (
      <div className={styles.status}>
        <Banner
          title="None of the data in this workspace has a device."
          description={
            <>
              To offer a model here, give the dataset a device, then add the
              robot description (.urdf) or point cloud (.ply) to that
              device&apos;s configurations, or to its device type&apos;s. To
              pick one kept in a dataset, use Search the whole organization.
            </>
          }
        />
      </div>
    );
  }

  const needle = term.trim().toLowerCase();
  const groups = workspace.devices.map((device, index) => {
    const data = models[index].data;
    return {
      device,
      deviceType: data?.deviceType ?? null,
      files: (data?.files ?? []).filter((record) =>
        record.relative_path.toLowerCase().includes(needle),
      ),
    };
  });

  if (groups.every((group) => group.files.length === 0)) {
    return (
      <div className={styles.status}>
        <RobotoTypography variant="body2" color="text.secondary">
          {needle === ""
            ? `No robot descriptions or point clouds among the configurations of ${groups
                .map((group) => group.device.deviceId)
                .join(
                  ", ",
                )}. Add a .urdf or .ply to the device's configurations, or to its device type's.`
            : `No robot descriptions or point clouds match “${term}”.`}
        </RobotoTypography>
      </div>
    );
  }

  const rows = (group: (typeof groups)[number]) =>
    group.files.map((record) => (
      <ModelFileRow
        key={record.file_id}
        record={record}
        location={
          group.deviceType !== null &&
          record.association_id === group.deviceType.device_type_id
            ? `${record.relative_path} · Inherited from ${group.deviceType.name}`
            : record.relative_path
        }
        resolving={resolving === record.file_id}
        // While one row is being resolved no other row may be chosen: a
        // second pick would race the first, and whichever lookup returned
        // last would win regardless of which the user asked for second.
        busy={resolving !== undefined}
        canIngest={canIngest}
        onPick={onPick}
        onIngest={onIngest}
      />
    ));

  if (groups.length === 1) {
    return <ul className={styles.list}>{rows(groups[0])}</ul>;
  }

  return (
    <>
      {groups
        // A device with nothing matching the term drops out with its heading;
        // with no term, every device is shown, saying so when it has nothing.
        .filter((group) => needle === "" || group.files.length > 0)
        .map((group) => (
          <section
            key={`${group.device.orgId}/${group.device.deviceId}`}
            className={styles.group}
            aria-label={group.device.deviceId}
          >
            <h2 className={styles.groupTitle}>{group.device.deviceId}</h2>
            {group.files.length === 0 ? (
              <RobotoTypography
                variant="body2"
                color="text.secondary"
                className={styles.groupNote}
              >
                No robot descriptions or point clouds among its configurations.
              </RobotoTypography>
            ) : (
              <ul className={styles.list}>{rows(group)}</ul>
            )}
          </section>
        ))}
    </>
  );
}
